/**
 * Locale root index: aggregates all locale namespace JSON files recursively.
 *
 * Pattern:
 * Each namespace folder contains `de.json` and `en.json`.
 * This file eagerly imports all `en.json` and `de.json` files via Vite globbing and constructs
 * the `en` and `de` message objects with deterministic key ordering.
 *
 * To add a new namespace:
 * 1. Create folder: `locales/<namespace>/`.
 * 2. Add `en.json` and `de.json`.
 * 3. Done – it is auto‑included.
 */

// Eagerly import JSON translations for both languages.
const enJsonModules = import.meta.glob("./**/en.json", {
  eager: true,
  import: "default",
}) as Record<string, unknown>;
const deJsonModules = import.meta.glob("./**/de.json", {
  eager: true,
  import: "default",
}) as Record<string, unknown>;

type NamespaceFromEnPath<P extends string> = P extends `./${infer N}/en.json`
  ? N
  : never;
type EnModulePaths = keyof typeof enJsonModules;
type NamespaceKeys = NamespaceFromEnPath<EnModulePaths & string>;

// Build the locale object given a module record and suffix.
function buildLocale<T extends Record<string, unknown>>(modules: T) {
  // Build a nested object from path segments.
  const root: Record<string, unknown> = {};
  const paths = Object.keys(modules).sort();
  for (const path of paths) {
    // Extract folder path between './' and '/<lang>.json'
    const folderPath = path.replace(/^\.\//, "").replace(/\/[^/]+\.json$/, "");
    const segments = folderPath.split("/").filter(Boolean);
    let cursor: Record<string, unknown> = root;
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      if (i === segments.length - 1) {
        // Assign translation object at leaf
        cursor[seg] = modules[path];
      } else {
        const existing = cursor[seg];
        if (
          !existing ||
          typeof existing !== "object" ||
          Array.isArray(existing)
        ) {
          cursor[seg] = {};
        }
        cursor = cursor[seg] as Record<string, unknown>;
      }
    }
  }
  return root as {
    [K in NamespaceKeys]: unknown;
  };
}

export const en = buildLocale(enJsonModules);
export const de = buildLocale(deJsonModules) as typeof en; // assume same structure as 'en'

export type MessageSchema = typeof en;
